(function () {
  'use strict';

  // ============================================================
  // SHOW CARDS DATA
  //
  // Responsibilities:
  // - Load show-cards-upload.json
  // - Fetch current Shopify product data
  // - Keep all sibling condition variants
  // - Apply SHOW inventory from the uploaded JSON
  // - Normalize everything into singles-grid-style rows
  // ============================================================

  if (
    !window.ShowCardsConfig ||
    !window.ShowCardsConfig.storefrontDomain
  ) {
    throw new Error(
      'ShowCardsConfig.storefrontDomain was not provided by page.show-cards.liquid.'
    );
  }


  const CONFIG = {
    manifestUrl: "\/\/flipsidegaming.com\/cdn\/shop\/t\/33\/assets\/show-cards-upload.json?v=98518456321955936051787143424",

    storefrontUrl:
      'https://' +
      window.ShowCardsConfig.storefrontDomain +
      '/api/2026-07/graphql.json',

    // We will tune this later if the show list becomes very large.
    batchSize: 50,

    // Singles products should only have a handful of condition variants.
    variantsPerProduct: 20
  };


  // ============================================================
  // GENERAL HELPERS
  // ============================================================

  function assertResponse(response, label) {
    if (!response.ok) {
      throw new Error(
        `${label} failed: HTTP ${response.status} ${response.statusText}`
      );
    }

    return response;
  }


  function normalizeId(value) {
    return String(value == null ? '' : value).trim();
  }


  function numericIdFromGid(gid) {
    return normalizeId(gid).replace(
      /^gid:\/\/shopify\/[^/]+\//,
      ''
    );
  }


  function chunkArray(items, size) {
    const chunks = [];

    for (let index = 0; index < items.length; index += size) {
      chunks.push(
        items.slice(index, index + size)
      );
    }

    return chunks;
  }


  function getConditionFromVariant(variant) {
    const options =
      variant && Array.isArray(variant.selectedOptions)
        ? variant.selectedOptions
        : [];

    const conditionOption = options.find(function (option) {
      return (
        String(option.name || '').trim().toLowerCase() ===
        'condition'
      );
    });

    if (conditionOption && conditionOption.value) {
      return conditionOption.value;
    }

    return variant && variant.title
      ? variant.title
      : '';
  }


  function moneyToCents(money) {
    if (!money || money.amount == null) {
      return null;
    }

    const amount = Number(money.amount);

    if (!Number.isFinite(amount)) {
      return null;
    }

    return Math.round(amount * 100);
  }


  function formatMoney(money) {
    if (!money || money.amount == null) {
      return null;
    }

    const amount = Number(money.amount);

    if (!Number.isFinite(amount)) {
      return null;
    }

    try {
      return new Intl.NumberFormat(
        undefined,
        {
          style: 'currency',
          currency: money.currencyCode || 'USD'
        }
      ).format(amount);

    } catch (error) {
      return '$' + amount.toFixed(2);
    }
  }


  // ============================================================
  // MANIFEST
  // ============================================================

  async function loadManifest() {
    const response = await fetch(
      CONFIG.manifestUrl,
      {
        cache: 'no-store'
      }
    );

    assertResponse(
      response,
      'Show cards upload request'
    );

    const manifest = await response.json();

    if (
      !manifest ||
      !Array.isArray(manifest.products)
    ) {
      throw new Error(
        'show-cards-upload.json does not contain a valid "products" array.'
      );
    }

    return manifest;
  }


  // ============================================================
  // SHOPIFY STOREFRONT
  // ============================================================

  async function fetchShopifyProducts(productGids) {
    if (
      !Array.isArray(productGids) ||
      productGids.length === 0
    ) {
      return [];
    }

    const query = `
      query ShowCardProducts(
        $ids: [ID!]!,
        $variantsFirst: Int!
      ) {
        nodes(ids: $ids) {
          ... on Product {
            id
            handle
            title
            onlineStoreUrl

            featuredImage {
              url(transform: { maxWidth: 800 })
              altText
            }

            variants(first: $variantsFirst) {
              nodes {
                id
                title
                availableForSale

                selectedOptions {
                  name
                  value
                }

                price {
                  amount
                  currencyCode
                }

                compareAtPrice {
                  amount
                  currencyCode
                }

                image {
                  url(transform: { maxWidth: 800 })
                  altText
                }
              }
            }
          }
        }
      }
    `;

    const response = await fetch(
      CONFIG.storefrontUrl,
      {
        method: 'POST',

        headers: {
          'Content-Type': 'application/json'
        },

        body: JSON.stringify({
          query: query,

          variables: {
            ids: productGids,
            variantsFirst: CONFIG.variantsPerProduct
          }
        })
      }
    );

    assertResponse(
      response,
      'Shopify Storefront request'
    );

    const result = await response.json();

    if (
      result.errors &&
      result.errors.length
    ) {
      console.error(
        '[Show Cards] Shopify GraphQL errors:',
        result.errors
      );

      throw new Error(
        result.errors
          .map(function (error) {
            return error.message;
          })
          .join(' | ')
      );
    }

    if (
      !result.data ||
      !Array.isArray(result.data.nodes)
    ) {
      throw new Error(
        'Shopify returned an unexpected response while loading show products.'
      );
    }

    return result.data.nodes.filter(Boolean);
  }


  async function fetchAllShopifyProducts(manifestProducts) {
    const productGids = manifestProducts
      .map(function (product) {
        return product.product_gid;
      })
      .filter(Boolean);

    const batches = chunkArray(
      productGids,
      CONFIG.batchSize
    );

    const allProducts = [];

    for (
      let index = 0;
      index < batches.length;
      index += 1
    ) {
      console.log(
        `[Show Cards] Loading Shopify batch ${index + 1} of ${batches.length}...`
      );

      const products =
        await fetchShopifyProducts(
          batches[index]
        );

      allProducts.push(...products);
    }

    const manifestOrder =
      new Map();

    manifestProducts.forEach(
      function (product, index) {
        if (
          !product ||
          !product.product_gid
        ) {
          return;
        }

        manifestOrder.set(
          normalizeId(
            product.product_gid
          ),
          index
        );
      }
    );

    allProducts.sort(
      function (a, b) {
        const aOrder =
          manifestOrder.has(
            normalizeId(a && a.id)
          )
            ? manifestOrder.get(
                normalizeId(a.id)
              )
            : Number.MAX_SAFE_INTEGER;

        const bOrder =
          manifestOrder.has(
            normalizeId(b && b.id)
          )
            ? manifestOrder.get(
                normalizeId(b.id)
              )
            : Number.MAX_SAFE_INTEGER;

        return aOrder - bOrder;
      }
    );

    return allProducts;
  }


  // ============================================================
  // SHOW INVENTORY LOOKUPS
  // ============================================================

  function buildManifestLookups(manifest) {
    const productsByGid = new Map();
    const variantsByGid = new Map();

    manifest.products.forEach(function (product) {
      if (!product || !product.product_gid) {
        return;
      }

      productsByGid.set(
        normalizeId(product.product_gid),
        product
      );

      const variants =
        Array.isArray(product.variants)
          ? product.variants
          : [];

      variants.forEach(function (variant) {
        if (!variant || !variant.variant_gid) {
          return;
        }

        variantsByGid.set(
          normalizeId(variant.variant_gid),
          {
            product: product,
            variant: variant
          }
        );
      });
    });

    return {
      productsByGid: productsByGid,
      variantsByGid: variantsByGid
    };
  }


  // ============================================================
  // NORMALIZE SHOPIFY → SINGLES ROWS
  // ============================================================

  function buildRows(
    manifest,
    shopifyProducts
  ) {
    const lookups =
      buildManifestLookups(manifest);

    const rows = [];


    shopifyProducts.forEach(function (product) {
      if (!product) {
        return;
      }

      const manifestProduct =
        lookups.productsByGid.get(
          normalizeId(product.id)
        );

      if (!manifestProduct) {
        console.warn(
          '[Show Cards] Shopify returned a product that was not found in the manifest:',
          product.id
        );

        return;
      }

      const variants =
        product.variants &&
        Array.isArray(product.variants.nodes)
          ? product.variants.nodes
          : [];


      variants.forEach(function (variant) {
        const manifestMatch =
          lookups.variantsByGid.get(
            normalizeId(variant.id)
          );

        /*
          THIS is the show inventory override.

          If this exact variant appears in
          show-cards-upload.json, use that quantity.

          Otherwise it still appears as a sibling
          condition, but it is Show OOS.
        */
        const showQuantity =
          manifestMatch
            ? Number(
                manifestMatch.variant.show_quantity || 0
              )
            : 0;

        const showAvailable =
          Number.isFinite(showQuantity) &&
          showQuantity > 0;


        /*
          Keep Shopify's own availability separately.

          The show page does NOT use it to determine
          whether the condition is in stock.
        */
        const shopifyAvailable =
          variant.availableForSale === true;


        const condition =
          getConditionFromVariant(variant);


        /*
          Prefer a variant image if Shopify has one.
          Otherwise fall back to the product image.
        */
        const image =
          variant.image ||
          product.featuredImage ||
          null;


        /*
          Mimic the normal collection JSON behavior:
          an unavailable SHOW variant has no active
          purchase price in the normalized row.

          Shopify's real price is still preserved
          separately for future use.
        */
        const currentPriceCents =
          moneyToCents(variant.price);

        const currentPriceDisplay =
          formatMoney(variant.price);


        rows.push({
          // --------------------------------------------
          // Product identity
          // --------------------------------------------

          id:
            numericIdFromGid(product.id),

          product_gid:
            normalizeId(product.id),

          variant_id:
            numericIdFromGid(variant.id),

          variant_gid:
            normalizeId(variant.id),

          handle:
            product.handle || '',

          product_url:
            product.onlineStoreUrl ||
            (
              product.handle
                ? '/products/' + product.handle
                : ''
            ),


                    // --------------------------------------------
          // Show / game identity
          // --------------------------------------------

          game:
            manifestProduct.game || '',

          tcgplayer_product_id:
            manifestProduct.tcgplayer_product_id || '',

          micro_id:
            manifestMatch
              ? manifestMatch.variant.micro_id || ''
              : '',

          show_selected:
            Boolean(manifestMatch),


          // --------------------------------------------
          // Shared singles card metadata
          //
          // These values come from the per-game
          // Shopify database CSV through
          // show-cards-upload.json.
          // --------------------------------------------

          set_code:
            manifestProduct.set_code || '',

          set_name:
            manifestProduct.set_name || '',

          rarity:
            manifestProduct.rarity || '',

          manacost:
            manifestProduct.manacost || '',

          colors:
            manifestProduct.colors || '',

          type:
            manifestProduct.type || '',  

          types:
            manifestProduct.types || '',

          subtypes:
            manifestProduct.subtypes || '',

          supertypes:
            manifestProduct.supertypes || '',

          power:
            manifestProduct.power || '',

          toughness:
            manifestProduct.toughness || '',

          life:
            manifestProduct.life || '',

          stage:
            manifestProduct.stage || '',

          evolves_from:
            manifestProduct.evolves_from || '',

          national_pokedex_numbers:
            manifestProduct.national_pokedex_numbers || '', 
            
          lore:
            manifestProduct.lore || '', 
            
          inkwell:
            manifestProduct.inkwell || '', 
            
          story:
            manifestProduct.story || '', 
            
          counter:
            manifestProduct.counter || '', 
            
          attribute:
            manifestProduct.attribute || '',   

          level:
            manifestProduct.level || '',

          zones:
            manifestProduct.zones || '',

          link:
            manifestProduct.link || '',

          flavor_text:
            manifestProduct.flavor_text || '',

          artist:
            manifestProduct.artist || '',

          number:
            manifestProduct.number || '',  

          finish:
            manifestProduct.finish || '',  


          /*
            Preserve every product metafield copied
            from the game's database.

            Game-specific renderers can use these
            later without requiring another change
            to this data loader.
          */
          metafields:
            manifestProduct.metafields &&
            typeof manifestProduct.metafields ===
              'object'
              ? manifestProduct.metafields
              : {},


          // --------------------------------------------
          // Basic Shopify display data
          // -------------------------------------------- --------------------------------------------

          img_url:
            image && image.url
              ? image.url
              : '',

          title:
            product.title || '',

          variant_title:
            variant.title || '',

          condition:
            condition,


          // --------------------------------------------
          // SHOW inventory
          // --------------------------------------------

          available:
            showAvailable,

          quantity:
            showAvailable
              ? showQuantity
              : 0,


          // --------------------------------------------
          // Preserve actual Shopify state separately
          // --------------------------------------------

          shopify_available:
            shopifyAvailable,


          // --------------------------------------------
          // Price
          // --------------------------------------------

          price:
            showAvailable
              ? currentPriceCents
              : null,

          price_display:
            showAvailable
              ? currentPriceDisplay
              : null,

          compare_at_price:
            showAvailable
              ? moneyToCents(
                  variant.compareAtPrice
                )
              : null,


          // Keep these for possible future show selling
          shopify_price:
            currentPriceCents,

          shopify_price_display:
            currentPriceDisplay
        });
      });
    });


    return rows;
  }


  // ============================================================
  // VALIDATION
  // ============================================================

  function validateHydration(
    manifest,
    shopifyProducts,
    rows
  ) {
    const returnedProductIds =
      new Set(
        shopifyProducts.map(function (product) {
          return normalizeId(
            product && product.id
          );
        })
      );


    const missingProducts =
      manifest.products.filter(function (product) {
        return !returnedProductIds.has(
          normalizeId(product.product_gid)
        );
      });


    const returnedVariantIds =
      new Set(
        rows.map(function (row) {
          return normalizeId(row.variant_gid);
        })
      );


    const missingSelectedVariants = [];

    manifest.products.forEach(function (product) {
      const variants =
        Array.isArray(product.variants)
          ? product.variants
          : [];

      variants.forEach(function (variant) {
        if (
          !returnedVariantIds.has(
            normalizeId(variant.variant_gid)
          )
        ) {
          missingSelectedVariants.push({
            product_gid:
              product.product_gid,

            handle:
              product.handle,

            variant_gid:
              variant.variant_gid,

            micro_id:
              variant.micro_id
          });
        }
      });
    });


    if (missingProducts.length) {
      console.warn(
        '[Show Cards] Manifest products not returned by Shopify:',
        missingProducts
      );
    }


    if (missingSelectedVariants.length) {
      console.warn(
        '[Show Cards] Selected show variants not returned by Shopify:',
        missingSelectedVariants
      );
    }


    return {
      missingProducts:
        missingProducts,

      missingSelectedVariants:
        missingSelectedVariants
    };
  }


  // ============================================================
  // MAIN LOAD
  // ============================================================

  async function load() {
    console.log(
      '[Show Cards] Loading show-cards-upload.json...'
    );

    const manifest =
      await loadManifest();


    console.log(
      `[Show Cards] Manifest contains ${manifest.products.length} products.`
    );


    const shopifyProducts =
      await fetchAllShopifyProducts(
        manifest.products
      );


    console.log(
      `[Show Cards] Shopify returned ${shopifyProducts.length} products.`
    );


    const rows =
      buildRows(
        manifest,
        shopifyProducts
      );


    const validation =
      validateHydration(
        manifest,
        shopifyProducts,
        rows
      );


    const result = {
      manifest:
        manifest,

      shopifyProducts:
        shopifyProducts,

      rows:
        rows,

      validation:
        validation
    };


    window.ShowCardsData.current =
      result;


    console.log(
      `[Show Cards] Built ${rows.length} normalized variant rows.`
    );

    console.log(
      '[Show Cards] Show rows:',
      rows
    );


    document.dispatchEvent(
      new CustomEvent(
        'showcards:data-ready',
        {
          detail: result
        }
      )
    );


    return result;
  }


  // ============================================================
  // PUBLIC API
  // ============================================================

  window.ShowCardsData = {
    load: load,
    current: null
  };

})();